import { useEffect, useMemo, createContext, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { useQueryParams } from 'hooks/useQueryParams'
import { uuid4 } from '@sentry/utils'

const AUTH_CHANNEL = 'blaze-auth-channel'
const AUTH_REFRESH_ALL_TABS_MESSAGE = 'refresh_all_tabs'

type AuthChannelContextType = {
  authChannel: BroadcastChannel
  tabId: string
} | null

const AuthChannelContext = createContext<AuthChannelContextType>(null)

export const AuthChannelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory()
  const { redirect_to: redirectToParam } = useQueryParams()
  const authChannel = useMemo(() => new BroadcastChannel(AUTH_CHANNEL), [])
  const tabId = useMemo(() => uuid4(), [])

  useEffect(() => {
    authChannel.onmessage = (event) => {
      if (event.data.type === AUTH_REFRESH_ALL_TABS_MESSAGE && event.data.tabId !== tabId) {
        if (redirectToParam) {
          history.push(redirectToParam)
          setTimeout(() => {
            history.go(0)
          }, 0)
        } else {
          history.go(0)
        }
      }
    }
  }, [authChannel, redirectToParam, history, tabId])

  return <AuthChannelContext.Provider value={{ authChannel, tabId }}>{children}</AuthChannelContext.Provider>
}

export const useAuthChannel = () => {
  const context = useContext(AuthChannelContext)
  if (!context) {
    throw new Error('useAuthChannel must be used within an AuthChannelProvider')
  }

  const refreshAllTabs = () => {
    context.authChannel.postMessage({
      type: AUTH_REFRESH_ALL_TABS_MESSAGE,
      tabId: context.tabId,
    })
  }

  return { refreshAllTabs }
}
